import { knownWords, spokenForm, wordKey } from '../lib/pronunciation.ts';
import type { VoiceSlot } from '../lib/characters.ts';
import type { LessonV2 } from '../lib/levels.ts';
import { audioTexts } from '../lib/lessonSteps.ts';
import { tokenize } from '../lib/text.ts';
import { teacherContent } from '../content/index.ts';
import type { BanterLine, TeacherContent } from '../types.ts';
import { feedbackPhrases } from '../lib/feedbackVoice.ts';
import { audioIds } from './audioIds.ts';

export interface AudioItem {
  id: string;
  text: string; // dokładnie to, co idzie do syntezy (z ewentualnymi znacznikami v3)
  lang: 'en' | 'pl';
  /** clean = czysty, przewidywalny model do zdań uczących; expressive = v3 do żartów i emocji */
  model: 'clean' | 'expressive';
  slow: boolean;
  kind: 'word' | 'banter' | 'teacher' | 'feedback' | 'lesson';
  /** Który głos ElevenLabs czyta nagranie: brak = główny. Drugi głos czyta drugą postać scenek (Emma) — `secondVoiceId` w voice.config.json. */
  voice?: VoiceSlot;
}

/**
 * Lista nagrań spoza lekcji kursu: słowa słownika wymowy, linijki bota, frazy nauczyciela i uwagi pod oceną (nagrania lekcji kursu: `planCourseAudio`).
 * Od patcha 49 nie ma tu zdań dawnych „dni” (`lessons.json` usunięty); ich pliki w `public/audio/` zostają, ale nikt ich już nie odtwarza.
 * Linijki bota są „expressive” (v3: żarty i emocje), reszta — czysty, przewidywalny model, bez znaczników emocji.
 *
 * Słowa: każde słowo ze słownika wymowy (`src/content/pronunciation.json`) ma nagranie normalne (`w.<słowo>`) i wolne (`w.<słowo>.slow`) — głosem ElevenLabs,
 * nie przeglądarki. Słowa lekcji kursu dokłada `planCourseAudio`.
 */
export function planAudio(banter: readonly BanterLine[], teacher: TeacherContent = teacherContent): AudioItem[] {
  const items = new Map<string, AudioItem>();
  const put = (it: AudioItem) => {
    if (!items.has(it.id)) items.set(it.id, it);
  };

  for (const w of planDictionaryWords()) {
    const text = spokenForm(w);
    put({ id: audioIds.word(w), text, lang: 'en', model: 'clean', slow: false, kind: 'word' });
    put({ id: audioIds.wordSlow(w), text, lang: 'en', model: 'clean', slow: true, kind: 'word' });
  }

  for (const b of banter) {
    put({
      id: audioIds.banter(b.id),
      text: b.text,
      lang: b.lang,
      model: b.lang === 'en' ? 'expressive' : 'clean',
      slow: false,
      kind: 'banter',
    });
    // polska wersja angielskiej linijki (tryb „uwagi bota po polsku”): ten sam bot, tekst z pola `pl`
    if (b.lang === 'en' && b.pl) put({ id: audioIds.banterPl(b.id), text: b.pl, lang: 'pl', model: 'clean', slow: false, kind: 'banter' });
  }

  // nauczyciel: frazy i pochwały w obu językach — zawsze „czysty” model (przewidywalny), bez znaczników emocji
  for (const [key, text] of Object.entries(teacher.phrases)) for (const lang of ['en', 'pl'] as const) put({ id: audioIds.teacher(key, lang), text: text[lang], lang, model: 'clean', slow: false, kind: 'teacher' });
  for (const p of teacher.praise) for (const lang of ['en', 'pl'] as const) put({ id: audioIds.teacher(p.id, lang), text: p[lang], lang, model: 'clean', slow: false, kind: 'teacher' });
  // uwagi bota pod oceną i w zadaniach, po polsku i po angielsku: ten sam tekst co na ekranie
  for (const f of feedbackPhrases()) put({ id: audioIds.feedback(f.key, f.lang), text: f.text, lang: f.lang, model: 'clean', slow: false, kind: 'feedback' });
  // zdania o nastroju z koła przed lekcją („I feel calm.”): bot czyta je, gdy koło się zatrzyma na buźce
  return [...items.values()];
}

/**
 * Słowa słownika wymowy (`pronunciation.json`): mają nagranie `w.<słowo>` niezależnie od lekcji. Razem ze słowami kursu (`planCourseWords`) to lista słów, które nauczyciel
 * może wymówić w uwadze („Usłyszałem add”): inne słowo (np. dziwny wynik rozpoznawania) nigdy nie padnie z głośnika.
 */
export function planDictionaryWords(): string[] {
  return [...new Set(knownWords().map((w) => wordKey(w)))].filter(Boolean).sort();
}

/** Opcje planu nagrań kursu v2. */
export interface CoursePlanOptions {
  /** Zdania z banku „Zmień zdanie” (`targets[].alts`) — dopóki aplikacja nie ma przycisku, nie nagrywamy ich (SPEC §23.14). */
  alts?: boolean;
  /** Nagrania „wolno” kwestii rozmówcy i drugiej roli (patch 50) oraz odpowiedzi „Co powiesz?” i pytań „Popatrz i wybierz” (patch 55). Domyślnie wyłączone — 🐢 przy nich mówi głos zapasowy (decyzje 19.10 i 20.10.2026). */
  partnerSlow?: boolean;
}

/** Słowa kursu v2, które mają nagranie `w.<słowo>`: cele, słowa dopisane w kroku 2 i każde słowo z tekstów do nagrania (dotknięcie słowa otwiera kartę „jak to czytać”). */
export function planCourseWords(lessons: readonly LessonV2[], opts: CoursePlanOptions = {}): string[] {
  const words = new Set<string>();
  for (const l of lessons) {
    for (const t of l.targets) {
      for (const part of t.word.split(/[\s/]+/)) words.add(wordKey(part));
      if (/\s/.test(t.word.trim()) && !t.word.includes('/')) words.add(wordKey(t.word)); // cel z kilku wyrazów („all right”): karta słowa gra całe wyrażenie
    }
    for (const e of l.enrich.extras) words.add(wordKey(e.word));
    for (const t of audioTexts(l)) {
      if (t.kind === 'alt' && !opts.alts) continue;
      for (const tok of tokenize(t.text)) words.add(wordKey(tok));
    }
  }
  return [...words].filter(Boolean).sort();
}

/**
 * Nagrania lekcji kursu v2: zdania opisu i wzbogacone, kwestie scenki (każda głosem swojej postaci), pytania do obrazka, karty „Co powiesz?” i części kwestii do klocków;
 * zdania, które dziecko powtarza, mają też wersję wolną (`.slow`). Do tego słowa (`w.<słowo>`, normalnie i wolno) z kart „jak to czytać”.
 * Słowa celów dostają osobne nagranie `w.<słowo>`, więc wpis `target` z `audioTexts` pomijamy.
 */
export function planCourseAudio(lessons: readonly LessonV2[], opts: CoursePlanOptions = {}): AudioItem[] {
  const items = new Map<string, AudioItem>();
  const put = (it: AudioItem): void => {
    if (!items.has(it.id)) items.set(it.id, it);
  };
  for (const l of lessons) {
    for (const t of audioTexts(l)) {
      if (t.kind === 'target' || (t.kind === 'alt' && !opts.alts)) continue;
      put({ id: audioIds.lesson(t.id), text: t.text, lang: 'en', model: 'clean', slow: false, kind: 'lesson', voice: t.voice });
      if (t.slow && (opts.partnerSlow || !t.slowExtra)) put({ id: audioIds.lessonSlow(t.id), text: t.text, lang: 'en', model: 'clean', slow: true, kind: 'lesson', voice: t.voice });
    }
  }
  for (const w of planCourseWords(lessons, opts)) {
    const text = spokenForm(w);
    put({ id: audioIds.word(w), text, lang: 'en', model: 'clean', slow: false, kind: 'word' });
    put({ id: audioIds.wordSlow(w), text, lang: 'en', model: 'clean', slow: true, kind: 'word' });
  }
  return [...items.values()];
}

export function totalChars(items: readonly AudioItem[]): number {
  return items.reduce((n, it) => n + it.text.length, 0);
}
